import { useCallback, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { DateTime } from 'luxon';
// @mui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TextField from '@mui/material/TextField';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
//
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useParams, useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { SplashScreen } from 'src/components/loading-screen';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { IftaFiling, IftaFilingLine } from 'src/models';
import HowToFileDialog from 'src/sections/report/how-to-file-dialog';
import FilesTab from 'src/sections/filing-wizard/files-tab';

// ----------------------------------------------------------------------

const NUM_SX = { fontVariantNumeric: 'tabular-nums' } as const;

function fmtMiles(value: number) {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function fmtGallons(value: number) {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Accounting notation: credits (negative tax) render as ($12.34) in green.
function Money({ value }: { value: number }) {
  if (value < 0) {
    return (
      <Box component="span" sx={{ ...NUM_SX, color: 'success.dark', fontWeight: 600 }}>
        (${Math.abs(value).toFixed(2)})
      </Box>
    );
  }
  return <Box component="span" sx={NUM_SX}>${value.toFixed(2)}</Box>;
}

export default function FilingReportPage() {
  const params = useParams();
  const router = useRouter();
  const filingId = params.id as string;
  const [filing, setFiling] = useState<IftaFiling | null>(null);
  const [selectedTruck, setSelectedTruck] = useState('all');
  const [filedDialogOpen, setFiledDialogOpen] = useState(false);
  const [markingFiled, setMarkingFiled] = useState(false);
  const [howToFileOpen, setHowToFileOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(formatEndpoint(endpoints.filings.report, { id: filingId }));
      setFiling(response.data.filing);
    } catch (error) {
      // 403 = not paid yet: send them back into the wizard
      router.replace(paths.filings.wizard(filingId));
    }
  }, [filingId, router]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadExcel = async () => {
    const response = await axios.get(
      formatEndpoint(endpoints.filings.reportExcel, { id: filingId }),
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `IFTA Filing ${filing?.selectedQuarter || ''}.xlsx`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const markFiled = async (filed: boolean) => {
    setMarkingFiled(true);
    try {
      const response = await axios.post(
        formatEndpoint(endpoints.filings.markFiled, { id: filingId }),
        { filed }
      );
      setFiling(response.data.filing);
      setFiledDialogOpen(false);
      enqueueSnackbar(filed ? 'Marked as filed' : 'Moved back to Calculated');
    } catch (error) {
      enqueueSnackbar('Could not update the filing status. Please try again.', {
        variant: 'error',
      });
    } finally {
      setMarkingFiled(false);
    }
  };

  if (!filing) {
    return <SplashScreen />;
  }

  const totalLine: IftaFilingLine = { ...filing.totalFilingLine, truckNumber: 'All Vehicles' };
  const selectedLine =
    selectedTruck === 'all'
      ? totalLine
      : filing.filingLines.find((line) => line.truckNumber === selectedTruck) || totalLine;
  const netTax = filing.totalFilingLine.totalTax;

  const truckOptions = [
    { id: 'all', label: 'All Vehicles' },
    ...filing.filingLines.map((line) => ({ id: line.truckNumber, label: `#${line.truckNumber}` })),
  ];
  const selectedOption = truckOptions.find((option) => option.id === selectedTruck) || truckOptions[0];

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="h4" data-testid="report-title" noWrap>
            {filing.title} · {filing.selectedQuarter}
          </Typography>
          <Label color={filing.filedAt ? 'success' : 'info'} data-testid="report-status">
            {filing.filedAt ? 'Filed' : 'Calculated'}
          </Label>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<Iconify icon="solar:paperclip-bold" />}
            onClick={() => setFilesOpen(true)}
            data-testid="uploaded-files-button"
          >
            Uploaded files
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Iconify icon="solar:download-bold" />}
            onClick={downloadExcel}
            data-testid="download-excel-button"
          >
            Download Excel
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ p: { xs: 3, md: 4 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={0.5}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5 }}>
              {netTax < 0 ? 'Net credit' : 'Net tax due'}
            </Typography>
            <Typography variant="h2" sx={NUM_SX} data-testid="report-total-tax">
              {netTax < 0 ? (
                <Box component="span" sx={{ color: 'success.dark' }}>
                  (${Math.abs(netTax).toFixed(2)})
                </Box>
              ) : (
                `$${netTax.toFixed(2)}`
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {filing.truckCount} truck{filing.truckCount === 1 ? '' : 's'} ·{' '}
              {filing.selectedQuarter}
              {filing.filedAt && (
                <>
                  {' · '}Filed on {DateTime.fromISO(filing.filedAt).toLocaleString(DateTime.DATE_MED)}
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => markFiled(false)}
                    disabled={markingFiled}
                    sx={{ ml: 1, minWidth: 0, color: 'text.secondary' }}
                    data-testid="unmark-filed-button"
                  >
                    Undo
                  </Button>
                </>
              )}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={4}>
            <HeroStat label="Total miles" value={fmtMiles(totalLine.totalMiles)} />
            <HeroStat label="Gallons purchased" value={fmtGallons(totalLine.totalTaxPaidGallons)} />
            <HeroStat label="Fleet MPG" value={totalLine.milesPerGallon.toFixed(3)} />
          </Stack>
        </Stack>
      </Card>

      <Card>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ px: 2.5, pt: 2.5, pb: 2 }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {selectedLine.truckNumber === 'All Vehicles'
              ? 'All Vehicles'
              : `Truck #${selectedLine.truckNumber}`}
            {' · '}
            {fmtMiles(selectedLine.totalMiles)} miles ·{' '}
            {fmtGallons(selectedLine.totalTaxPaidGallons)} gal purchased ·{' '}
            {selectedLine.milesPerGallon.toFixed(3)} MPG
          </Typography>

          <Autocomplete
            options={truckOptions}
            value={selectedOption}
            onChange={(_, option) => setSelectedTruck(option.id)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableClearable
            size="small"
            sx={{ width: { xs: '100%', sm: 240 } }}
            renderInput={(params) => (
              <TextField {...params} label="Vehicle" data-testid="truck-select" />
            )}
          />
        </Stack>

        <StateLedger line={selectedLine} />
      </Card>

      {!filing.filedAt && (
        <Card sx={{ p: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">
                Next step: file with your base jurisdiction
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Submit these numbers to your base state, then confirm here so this filing moves to
                Filed.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<Iconify icon="solar:question-circle-bold" />}
                onClick={() => setHowToFileOpen(true)}
                data-testid="how-to-file-button"
              >
                How to file
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                onClick={() => setFiledDialogOpen(true)}
                data-testid="mark-filed-button"
              >
                I filed this return
              </Button>
            </Stack>
          </Stack>
        </Card>
      )}

      <HowToFileDialog open={howToFileOpen} onClose={() => setHowToFileOpen(false)} />

      <Dialog open={filesOpen} onClose={() => setFilesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Uploaded files</DialogTitle>
        <DialogContent sx={{ px: { xs: 2, md: 4 } }}>
          <FilesTab filing={filing} />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFilesOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={filedDialogOpen}
        onClose={() => setFiledDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Did you file this return?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Confirm once you have submitted this quarter&apos;s IFTA return to your base
            jurisdiction. The filing moves from Calculated to Filed, and you can undo this later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFiledDialogOpen(false)}>
            Not yet
          </Button>
          <LoadingButton
            variant="contained"
            color="success"
            loading={markingFiled}
            startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            onClick={() => markFiled(true)}
            data-testid="confirm-filed-button"
          >
            Yes, I filed it
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={NUM_SX}>
        {value}
      </Typography>
    </Stack>
  );
}

function StateLedger({ line }: { line: IftaFilingLine }) {
  const hasSurcharge = line.stateLines.some((stateLine) => stateLine.overcharge);
  // States alphabetical, each surcharge line directly under its state, like the
  // official IFTA return.
  const sortedLines = [...line.stateLines].sort(
    (a, b) => a.state.localeCompare(b.state) || Number(a.overcharge) - Number(b.overcharge)
  );

  return (
    <TableContainer data-testid="report-table">
      <Table
        size="small"
        sx={{
          '& th:first-of-type, & td:first-of-type': { pl: 2.5 },
          '& th:last-of-type, & td:last-of-type': { pr: 2.5 },
        }}
      >
        <TableHead>
          <TableRow>
            <HeadCell>State</HeadCell>
            <HeadCell align="right">Total miles</HeadCell>
            <HeadCell align="right">Taxable miles</HeadCell>
            <HeadCell align="right">Taxable gal</HeadCell>
            <HeadCell align="right">Tax paid gal</HeadCell>
            <HeadCell align="right">Net taxable gal</HeadCell>
            <HeadCell align="right">Tax</HeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedLines.map((stateLine, index) => (
            <TableRow key={index} hover>
              <TableCell>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {stateLine.state}
                </Box>
                {stateLine.overcharge && (
                  <Box component="sup" sx={{ color: 'text.secondary', ml: 0.25 }}>
                    †
                  </Box>
                )}
              </TableCell>
              <TableCell align="right" sx={NUM_SX}>{fmtMiles(stateLine.totalMiles)}</TableCell>
              <TableCell align="right" sx={NUM_SX}>{fmtMiles(stateLine.taxableMiles)}</TableCell>
              <TableCell align="right" sx={NUM_SX}>{fmtGallons(stateLine.taxableGallons)}</TableCell>
              <TableCell align="right" sx={NUM_SX}>{fmtGallons(stateLine.taxPaidGallons)}</TableCell>
              <TableCell align="right" sx={NUM_SX}>{fmtGallons(stateLine.netTaxableGallons)}</TableCell>
              <TableCell align="right">
                <Money value={stateLine.tax} />
              </TableCell>
            </TableRow>
          ))}

          <TableRow
            sx={{
              '& td': {
                fontWeight: 700,
                borderTop: (theme) => `3px double ${theme.palette.text.primary}`,
                borderBottom: 'none',
              },
            }}
          >
            <TableCell>Total</TableCell>
            <TableCell align="right" sx={NUM_SX}>{fmtMiles(line.totalMiles)}</TableCell>
            <TableCell align="right" sx={NUM_SX}>{fmtMiles(line.totalTaxableMiles)}</TableCell>
            <TableCell align="right" sx={NUM_SX}>{fmtGallons(line.totalTaxableGallons)}</TableCell>
            <TableCell align="right" sx={NUM_SX}>{fmtGallons(line.totalTaxPaidGallons)}</TableCell>
            <TableCell align="right" sx={NUM_SX}>{fmtGallons(line.totalNetTaxableGallons)}</TableCell>
            <TableCell align="right">
              <Money value={line.totalTax} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Stack spacing={0.25} sx={{ px: 2.5, py: 2 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Tax amounts in parentheses are credits for fuel purchased in that state beyond what you
          burned there.
        </Typography>
        {hasSurcharge && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            † Surcharge line: this state collects an additional per-gallon surcharge on top of its
            fuel tax.
          </Typography>
        )}
      </Stack>
    </TableContainer>
  );
}

function HeadCell({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <TableCell
      align={align}
      sx={{ typography: 'caption', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}
    >
      {children}
    </TableCell>
  );
}
